<template>
  <div class="live-chat-cta-v2-dialog">
    <v-form ref="form" v-model="isFormValid">
      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.live_chat_cta_v2.content") }}</h4>

        <v-text-field
          v-model="formData.line1"
          :label="
            formData.twoLine
              ? $t('shortcodes.live_chat_cta_v2.button_text_line_1')
              : $t('shortcodes.live_chat_cta_v2.button_text')
          "
          :placeholder="$t('shortcodes.live_chat_cta_v2.button_text_line_1')"
          :rules="[v => !!(v && v.trim()) || $t('shortcodes.live_chat_cta_v2.button_text_line_1_required')]"
          required />

        <v-switch
          v-model="formData.twoLine"
          :label="$t('shortcodes.live_chat_cta_v2.use_two_lines')"
          class="mt-0" />

        <v-text-field
          v-if="formData.twoLine"
          v-model="formData.line2"
          :label="$t('shortcodes.live_chat_cta_v2.button_text_line_2')"
          :placeholder="$t('shortcodes.live_chat_cta_v2.button_text_line_2')"
          :rules="[v => !formData.twoLine || !!(v && v.trim()) || $t('shortcodes.live_chat_cta_v2.button_text_line_2_required')]"
          required />

        <v-text-field
          v-model="formData.url"
          :label="$t('shortcodes.live_chat_cta_v2.url')"
          placeholder="tel:+1234567890"
          :rules="[v => !!(v && v.trim()) || $t('shortcodes.live_chat_cta_v2.url_required')]"
          required />
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.live_chat_cta_v2.icon") }}</h4>

        <v-switch
          v-model="formData.useDefaultIcon"
          :label="$t('shortcodes.live_chat_cta_v2.use_default_icon')"
          class="mt-0" />

        <v-text-field
          v-if="!formData.useDefaultIcon"
          v-model="formData.iconSrc"
          :label="$t('shortcodes.live_chat_cta_v2.icon_url')"
          placeholder="https://example.com/icon.svg" />
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.live_chat_cta_v2.appearance") }}</h4>

        <VaColorPicker
          v-model="formData.bgColor"
          :title="$t('shortcodes.live_chat_cta_v2.button_color')"
          position="bottom"
          class="mb-4" />
        <VaColorPicker
          v-model="formData.textColor"
          :title="$t('shortcodes.live_chat_cta_v2.text_color')"
          position="bottom"
          class="mb-4" />

        <v-select
          v-model="formData.fontFamily"
          :items="fontFamilyOptions"
          :label="$t('shortcodes.live_chat_cta_v2.font_family')"
          :menu-props="{ offsetY: true }"
          class="mb-2" />

        <v-select
          v-model="formData.fontSize"
          :items="fontSizeOptions"
          :label="
            formData.twoLine
              ? $t('shortcodes.live_chat_cta_v2.font_size_line_1')
              : $t('shortcodes.live_chat_cta_v2.font_size')
          "
          :menu-props="{ offsetY: true }"
          class="mb-2" />

        <v-select
          v-if="formData.twoLine"
          v-model="formData.fontSize2"
          :items="fontSizeOptions"
          :label="$t('shortcodes.live_chat_cta_v2.font_size_line_2')"
          :menu-props="{ offsetY: true }"
          class="mb-2" />
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.live_chat_cta_v2.options") }}</h4>

        <v-switch
          v-model="formData.pulsingAnimation"
          :label="$t('shortcodes.live_chat_cta_v2.pulsing_animation')"
          class="mt-0" />
        <v-switch
          v-model="formData.spaceBellow"
          :label="$t('shortcodes.live_chat_cta_v2.space_below')"
          class="mt-0" />
      </div>
    </v-form>
  </div>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import { LIVE_CHAT_CTA_V2_EVENT_TYPES, LIVE_CHAT_CTA_V2_FONT_FAMILIES, LIVE_CHAT_CTA_V2_FONT_SIZES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker";

  const DEFAULT_ICON_SRC = `${process.env.VUE_APP_API}/images/phone.svg`;

  const DEFAULT_FORM_DATA = {
    line1: "",
    line2: "",
    twoLine: false,
    useDefaultIcon: true,
    iconSrc: "",
    bgColor: "#28b02b",
    textColor: "#ffffff",
    fontFamily: LIVE_CHAT_CTA_V2_FONT_FAMILIES[0].value,
    fontSize: "18px",
    fontSize2: "18px",
    url: "",
    pulsingAnimation: false,
    spaceBellow: false,
  };

  export default {
    name: "LiveChatCTAV2Dialog",
    components: { VaColorPicker },
    data() {
      return {
        isFormValid: false,
        fontFamilyOptions: LIVE_CHAT_CTA_V2_FONT_FAMILIES,
        fontSizeOptions: LIVE_CHAT_CTA_V2_FONT_SIZES,
        formData: cloneDeep(DEFAULT_FORM_DATA),
      };
    },
    computed: {
      resolvedIconSrc() {
        return this.formData.useDefaultIcon ? DEFAULT_ICON_SRC : (this.formData.iconSrc || "").trim();
      },
    },
    watch: {
      isFormValid: {
        handler() {
          this.sendError();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isFormValid) {
            this.updateValue();
          }
        }
      },
      sendError() {
        window.parent.postMessage(
          {
            mceAction: LIVE_CHAT_CTA_V2_EVENT_TYPES.VALIDATION,
            data: { isValid: this.isFormValid },
          },
          origin,
        );
      },
      updateValue() {
        const { useDefaultIcon, ...rest } = this.formData;
        window.parent.postMessage(
          {
            mceAction: LIVE_CHAT_CTA_V2_EVENT_TYPES.UPDATE_LIVE_CHAT_CTA_V2_DATA,
            data: { ...rest, iconSrc: this.resolvedIconSrc },
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === LIVE_CHAT_CTA_V2_EVENT_TYPES.NEED_VALIDATION) {
            await this.$refs.form.validate();
          }
          if (message.data.type === LIVE_CHAT_CTA_V2_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data && Object.keys(data).length > 0) {
              const usesDefaultIcon = data.iconSrc === DEFAULT_ICON_SRC;
              this.formData = {
                ...DEFAULT_FORM_DATA,
                ...data,
                useDefaultIcon: usesDefaultIcon,
                iconSrc: usesDefaultIcon ? "" : data.iconSrc || "",
                fontFamily: data.fontFamily || DEFAULT_FORM_DATA.fontFamily,
                fontSize: data.fontSize || DEFAULT_FORM_DATA.fontSize,
                fontSize2: data.fontSize2 || data.fontSize || DEFAULT_FORM_DATA.fontSize2,
              };
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage({ mceAction: LIVE_CHAT_CTA_V2_EVENT_TYPES.REQUEST_DATA }, origin);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .live-chat-cta-v2-dialog {
    margin-bottom: 30px;
    padding: 30px 12px 0;
  }

  .section {
    margin-top: 24px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;

    h4 {
      margin: 0;
      font-weight: 500;
    }
  }
</style>
